/**
 * GTS_SUNUM_MOCK — Pazaryeri sunum siparişleri (DB/API yok).
 * Her pazaryerinden 2 sipariş, hepsi "Onay Bekleyen" (pending).
 * "Sipariş Alındı" (received) sekmesi e-ticaret siparişleri içindir.
 */
import { loadDemoOrderStatuses } from './demoMode';

type DemoOrderStatus =
  | 'received'
  | 'pending'
  | 'confirmed'
  | 'shipped'
  | 'delivered'
  | 'cancel_requested'
  | 'cancelled';

type DemoPlatform = 'trendyol' | 'hepsiburada' | 'n11' | 'pazarama';

const PENDING_LABEL = 'Onay Bekleyen';

export interface DemoMarketplaceOrder {
  id: string;
  orderId: string;
  orderNumber: string;
  memberId: string;
  memberName: string;
  memberEmail: string;
  customerName: string;
  customerEmail: string;
  date: string;
  status: DemoOrderStatus;
  totalAmount: number;
  itemsTotal: number;
  items: Array<{
    productId: string;
    name: string;
    image: string;
    price: number;
    quantity: number;
    lineId?: number;
    n11LineId?: string;
    n11OrderLineId?: string;
    lineItemId?: string;
    orderItemId?: string;
    variantId?: string;
  }>;
  shippingAddress: {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    district: string;
    phone: string;
    title: string;
  };
  marketplace: DemoPlatform;
  n11OrderId?: string;
  n11Status?: string;
  trackingNumber?: string;
  shippingCompany?: string;
  shipmentPackageStatus?: string;
  cargoInfo?: {
    cargoTrackingNumber?: string;
    cargoProviderName?: string;
    cargoTrackingLink?: string;
  };
}

interface DemoOrderSeed {
  platform: DemoPlatform;
  slot: 1 | 2;
  orderNumber: string;
  packageId: string;
  customerName: string;
  customerEmail: string;
  productName: string;
  price: number;
  city: string;
  district: string;
}

const DEMO_ORDER_SEEDS: DemoOrderSeed[] = [
  {
    platform: 'trendyol',
    slot: 1,
    orderNumber: 'TY-1045821',
    packageId: '8801045821',
    customerName: 'Elif Yılmaz',
    customerEmail: 'elif.yilmaz@demo.mail',
    productName: 'Kablosuz Kulaklık Pro X',
    price: 899.9,
    city: 'İstanbul',
    district: 'Üsküdar',
  },
  {
    platform: 'trendyol',
    slot: 2,
    orderNumber: 'TY-1045903',
    packageId: '8801045903',
    customerName: 'Burak Demir',
    customerEmail: 'burak.demir@demo.mail',
    productName: 'Erkek Polar Mont L',
    price: 649.5,
    city: 'Ankara',
    district: 'Çankaya',
  },
  {
    platform: 'hepsiburada',
    slot: 1,
    orderNumber: 'HB-48291055',
    packageId: '99048291055',
    customerName: 'Selin Aktaş',
    customerEmail: 'selin.aktas@demo.mail',
    productName: 'Robot Süpürge 3000X',
    price: 12499.0,
    city: 'İzmir',
    district: 'Bornova',
  },
  {
    platform: 'hepsiburada',
    slot: 2,
    orderNumber: 'HB-48291502',
    packageId: '99048291502',
    customerName: 'Oğuz Kılıç',
    customerEmail: 'oguz.kilic@demo.mail',
    productName: 'Çelik Tencere Seti 7 Parça',
    price: 2199.0,
    city: 'Bursa',
    district: 'Nilüfer',
  },
  {
    platform: 'n11',
    slot: 1,
    orderNumber: 'N11-778201',
    packageId: '770778201',
    customerName: 'Merve Çelik',
    customerEmail: 'merve.celik@demo.mail',
    productName: 'Akıllı Saat Fit V2',
    price: 1599.0,
    city: 'Antalya',
    district: 'Muratpaşa',
  },
  {
    platform: 'n11',
    slot: 2,
    orderNumber: 'N11-778245',
    packageId: '770778245',
    customerName: 'Kerem Öztürk',
    customerEmail: 'kerem.ozturk@demo.mail',
    productName: 'Gaming Mouse RGB',
    price: 749.9,
    city: 'Kocaeli',
    district: 'İzmit',
  },
  {
    platform: 'pazarama',
    slot: 1,
    orderNumber: 'PZ-330184',
    packageId: '660330184',
    customerName: 'Deniz Arslan',
    customerEmail: 'deniz.arslan@demo.mail',
    productName: 'Bluetooth Hoparlör Mini',
    price: 429.0,
    city: 'Adana',
    district: 'Seyhan',
  },
  {
    platform: 'pazarama',
    slot: 2,
    orderNumber: 'PZ-330219',
    packageId: '660330219',
    customerName: 'Gizem Şahin',
    customerEmail: 'gizem.sahin@demo.mail',
    productName: 'Çocuk Bisikleti 20 Jant',
    price: 2899.0,
    city: 'Gaziantep',
    district: 'Şehitkamil',
  },
];

function buildItems(seed: DemoOrderSeed) {
  const idx = seed.slot;

  if (seed.platform === 'trendyol') {
    return [
      {
        productId: `demo-ty-prod-${idx}`,
        name: seed.productName,
        image: '/api/placeholder/80/80',
        price: seed.price,
        quantity: 1,
        lineId: 2000 + idx,
      },
    ];
  }

  if (seed.platform === 'n11') {
    return [
      {
        productId: `demo-n11-prod-${idx}`,
        name: seed.productName,
        image: '/api/placeholder/80/80',
        price: seed.price,
        quantity: 1,
        n11LineId: String(60000 + idx),
        n11OrderLineId: String(60000 + idx),
      },
    ];
  }

  if (seed.platform === 'hepsiburada') {
    return [
      {
        productId: `demo-hb-prod-${idx}`,
        name: seed.productName,
        image: '/api/placeholder/80/80',
        price: seed.price,
        quantity: 1,
        lineItemId: `hb-line-${seed.orderNumber}`,
      },
    ];
  }

  const orderItemId = `pz-item-${seed.orderNumber}`;
  return [
    {
      productId: `demo-pz-prod-${idx}`,
      name: seed.productName,
      image: '/api/placeholder/80/80',
      price: seed.price,
      quantity: 1,
      orderItemId,
      variantId: `pz-item-${orderItemId}`,
    },
  ];
}

function buildOrder(seed: DemoOrderSeed): DemoMarketplaceOrder {
  const id = `demo-${seed.platform}-${seed.slot}`;
  const [firstName, ...rest] = seed.customerName.split(' ');
  const dayMs = (seed.slot + ['trendyol', 'hepsiburada', 'n11', 'pazarama'].indexOf(seed.platform) * 2) * 3_600_000;

  return {
    id,
    orderId: id,
    orderNumber: seed.orderNumber,
    memberId: `${seed.platform}_demo_${seed.slot}`,
    memberName: seed.customerName,
    memberEmail: seed.customerEmail,
    customerName: seed.customerName,
    customerEmail: seed.customerEmail,
    date: new Date(Date.now() - dayMs).toISOString(),
    status: 'pending',
    totalAmount: seed.price,
    itemsTotal: seed.price,
    items: buildItems(seed),
    shippingAddress: {
      firstName: firstName || 'Demo',
      lastName: rest.join(' ') || 'Müşteri',
      address: `${seed.district} Mah. Örnek Cad. No:${10 + seed.slot}`,
      city: seed.city,
      district: seed.district,
      phone: `0555${String(1000000 + seed.slot * 11111).slice(0, 7)}`,
      title: 'Ev',
    },
    marketplace: seed.platform,
    n11OrderId: seed.packageId,
    n11Status: PENDING_LABEL,
    shipmentPackageStatus: PENDING_LABEL,
  };
}

const STATUS_LABELS: Record<DemoOrderStatus, string> = {
  received: 'Sipariş Alındı',
  pending: PENDING_LABEL,
  confirmed: 'Hazırlanıyor',
  shipped: 'Kargoda',
  delivered: 'Teslim Edildi',
  cancel_requested: 'İptal Talebi',
  cancelled: 'İptal Edildi',
};

export function getDemoMarketplaceOrders(companyId: string): DemoMarketplaceOrder[] {
  const overrides = loadDemoOrderStatuses(companyId);

  return DEMO_ORDER_SEEDS.map((seed) => {
    const order = buildOrder(seed);
    const override = overrides[order.id];
    if (override) {
      order.status = override as DemoOrderStatus;
      const label = STATUS_LABELS[order.status] || override;
      order.n11Status = label;
      order.shipmentPackageStatus = label;
    }
    return order;
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}
